import React from 'react';

import styled from 'styled-components';

import { Button } from 'components/Button';
import { colors } from 'theme/colors';
import AddEntityModal from 'components/Entities/AddEntityModal/AddEntityModal';
import CreateFolderModal from 'containers/Dashboard/components/SideBar/components/Modals/CreateFolderModal';
import { useStore } from 'utils/hooks/store';
import { UserRole } from 'core/constants';
import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 48px;
  text-align: center;
  gap: 24px;
`;

const StyledTitle = styled.p`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 20px;
  font-weight: 500;
  margin: 0;
`;

const StyledDescription = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  margin: 0;
`;

const EmptyStateIcon = (): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" fill="none">
    <path
      d="M70.8333 80.0832H20.8333V29.1666H50V20.8333H20.8333C16.25 20.8333 12.5 24.5833 12.5 29.1666V79.1666C12.5 83.7499 16.25 87.4999 20.8333 87.4999H70.8333C75.4167 87.4999 79.1667 83.7499 79.1667 79.1666V49.9999H70.8333V80.0832Z"
      fill="#4BA0FD"
    />
    <path
      d="M79.1641 8.33325H70.8307V20.8333H58.3307C58.3724 20.8749 58.3307 29.1666 58.3307 29.1666H70.8307V41.6249C70.8724 41.6666 79.1641 41.6249 79.1641 41.6249V29.1666H91.6641V20.8333H79.1641V8.33325ZM29.1641 37.4999H62.4974V45.8333H29.1641V37.4999ZM29.1641 49.9999V58.3333H62.4974V49.9999H49.9974H29.1641ZM29.1641 62.4999H62.4974V70.8332H29.1641V62.4999Z"
      fill={colors.primary.main}
    />
  </svg>
);

const EmptyDocumentsMessage: React.FC<{ withoutFolders: boolean }> = ({ withoutFolders }) => {
  const { documentsDetails, folders, session } = useStore();
  const canManageFolders = ADMIN_AND_MANAGERS.includes(session?.user?.role as UserRole);
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = React.useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = React.useState(false);

  const handleCreateDocument = (): void => {
    setIsCreateDocumentModalOpen(true);
  };

  const handleCreateFolder = (): void => {
    setIsCreateFolderModalOpen(true);
  };

  const handleCloseCreateFolderModal = (): void => {
    setIsCreateFolderModalOpen(false);
  };

  const handleSubmitCreateFolder = async (name: string, parent: string | null): Promise<void> => {
    await folders.createFolder(name, parent);
    setIsCreateFolderModalOpen(false);
  };

  return (
    <StyledContainer>
      <EmptyStateIcon />
      <div>
        <StyledTitle>{withoutFolders ? 'No folders yet' : 'No documents yet'}</StyledTitle>
        <StyledDescription>
          {withoutFolders
            ? canManageFolders
              ? 'Create your first folder to get started.'
              : 'No folders have been created yet.'
            : 'Create your first document to get started.'}
        </StyledDescription>
      </div>
      {withoutFolders ? (
        <>
          {canManageFolders && (
            <>
              <Button variant="contained" onClick={handleCreateFolder}>
                Create folder
              </Button>
              <CreateFolderModal
                open={isCreateFolderModalOpen}
                onClose={handleCloseCreateFolderModal}
                onCreateFolder={handleSubmitCreateFolder}
                folders={folders.flatFolders}
              />
            </>
          )}
        </>
      ) : (
        <>
          <Button variant="contained" onClick={handleCreateDocument}>
            Create document
          </Button>
          {isCreateDocumentModalOpen && (
            <AddEntityModal onClose={() => setIsCreateDocumentModalOpen(false)} store={documentsDetails} />
          )}
        </>
      )}
    </StyledContainer>
  );
};

export default EmptyDocumentsMessage;
